import { faCaretDown, faCaretRight, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { unwrapResult } from '@reduxjs/toolkit';
import { geoAlbers, geoPath } from 'd3-geo';
import Fuse from 'fuse.js';
import * as React from 'react';
import Select from 'react-select';
import {
    Button, Card, CardBody, CardHeader, Col, Collapse, Input, Label, ListGroup,
    ListGroupItem, Modal, ModalBody, ModalFooter, ModalHeader, Row
} from "reactstrap";
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { CountyPopulation, EntityGeoLocation, getCountyPopulations, getEntityGeoLocations } from '../../../redux/AddressSlice';
import { Afr, getSelectedAfr, resetState } from '../../../redux/AfrSlice';
import { resetConceptState } from '../../../redux/ConceptSlice';
import { Affiliation, EditEntityQuery, Entity, createNameChangeEntry, getSelectedEntity, hasNameChange, loadEntity, resetEntityState, selectEntityStateStatus } from '../../../redux/EntitySlice';
import { setIsDirty, setTaxonomyType } from '../../../redux/MiscSlice';
import { TaxonomyTypeEnum } from "../../../redux/TaxonomyVersionSlice";
import { getGeoJSONData, selectGeoJSONData } from '../../../redux/VisualizationSlice';
import utilityHelper from '../../../utils/utilityHelper';
import AfrReportsCore from "../../AfrCore/AfrReportsCore";
import AfrSelectorDropDown from "../AfrSelectorDropDown";
import "./GeoLocation.css";
import { Superscript } from "react-bootstrap-icons";

export interface entityListItem {
    name: string;
    value: string;
    entityType: "COUNTY" | "CITY" | "SPECIAL_DISTRICT" | "OTHER_ENTITY" | "UNKNOWN"
}

interface GeoLocationMapProps {
    setSelectedReportsModule: (module: 'SUBMITTED' | 'SYSTEM' | 'ADHOC' | 'HOME') => void;
    setPreSelectedEntityList: (entityList: Array<string>) => void;
}

const GeoLocationMap = (props: GeoLocationMapProps) => {

    const dispatch = useAppDispatch();

    //selectors
    const geoJSONSelector = useAppSelector(selectGeoJSONData);
    const afrSelector: Afr | null = useAppSelector(getSelectedAfr);
    const selectedEntitySelector: Entity | null = useAppSelector(getSelectedEntity);
    const entityStateStatusSelector = useAppSelector(selectEntityStateStatus);

    //hooks
    const [mapData, setMapData] = React.useState<any>([]);
    const [width, setWidth] = React.useState(900);
    const [height, setHeight] = React.useState(900);

    const [modalIsOpen, setModalIsOpen] = React.useState<boolean>(false);
    const [entityName, setEntityName] = React.useState<string>('');
    const [existingEntityOptions, setExistingEntityOptions] = React.useState<entityListItem[]>([]);
    const [searchResults, setSearchResults] = React.useState<entityListItem[]>(existingEntityOptions);

    const [countyCount, setCountyCount] = React.useState<number>(0);
    const [cityCount, setCityCount] = React.useState<number>(0);
    const [specialDistrictCount, setSpecialDistrictCount] = React.useState<number>(0);
    const [otherEntityCount, setOtherEntityCount] = React.useState<number>(0);

    const [countiesAreCollapsed, setCountiesAreCollapsed] = React.useState<boolean>(true);
    const [citiesAreCollapsed, setCitiesAreCollapsed] = React.useState<boolean>(true);
    const [specialDistrictsAreCollapsed, setSpecialDistrictsAreCollapsed] = React.useState<boolean>(true);
    const [otherEntitiesAreCollapsed, setOtherEntitiesAreCollapsed] = React.useState<boolean>(true);

    const [selectedEntityCode, setSelectedEntityCode] = React.useState<string>('');
    const [selectedEntityNameValue, setSelectedEntityNameValue] = React.useState<string>('');

    const svgRef = React.useRef<SVGSVGElement | null>(null);

    //entity geo location data
    const [entitiesByCounty, setEntitiesByCounty] = React.useState<Array<EntityGeoLocation>>([]);
    const [selectedCountyName, setSelectedCountyName] = React.useState<string>("");

    const [selectedDropDownItem, setSelectedDropDownItem] = React.useState<{ value: string; label: string } | null>(null);
    const [entityOptions, setEntityOptions] = React.useState<Array<{ value: string; label: string }>>([]);

    const [isLoading, setIsLoading] = React.useState<boolean>(true);

    //census data
    const [censusPopulation, setCensusPopulation] = React.useState<Array<CountyPopulation> | null>(null);
    const [selectedCensusPopulation, setSelectedCensusPopulation] = React.useState<string>("");

    var entitySortKeys: any = {};

    /*START OF COPIED SUBMITTED REPORTS*/
    const getNameChangeText = function (entity: Entity): string {
        var result: string = "";
        if (entity.references) {
            entity.references.map(x => (x.type.indexOf("nameChange") > 0) ? result = createNameChangeEntry(x) : "");
        }
        return result;
    }
    const matchesAffiliation = function (entity: Entity | null, afr: Afr | null): boolean {
        if (!entity || !afr || !afr.owner) {
            return false;
        }
        var eCode: string = afr.owner.code;

        var affToMatch: Affiliation | null = entity.affiliation;
        while (affToMatch) {
            if (affToMatch.affiliate.code === eCode) {
                return true;
            }
            affToMatch = affToMatch.prior;
        }
        return false;
    }
  

    /*END OF COPIED SUBMITTED REPORTS*/

    //functions
    const postProcessJSON = async () => {
        await dispatch(getGeoJSONData());
    }

    // show tooltip when hovering over a region
    const handleMouseOver = function (tooltipData: any) {

        var moneyAmount = tooltipData.ACCOUNT_VALUE ? parseInt(tooltipData.ACCOUNT_VALUE).toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,

        }) : "$0";
    };

    // hide tooltip as mouse leaves region
    const handleMouseOut = function () {
    };


    const handleFill = function (feature: any) {
        return "rgb(119, 152, 187)";
    };

    const toggleModal = () => {
        setModalIsOpen(!modalIsOpen);
    }

    // Click handler
    const handlePathClick = (i: number) => {

            setEntityName('');
            setCountiesAreCollapsed(true);
            setCitiesAreCollapsed(true);
            setSpecialDistrictsAreCollapsed(true);
            setOtherEntitiesAreCollapsed(true);

            let selectedCountyNameString: string = mapData[i].properties.NAME.toString();

            let foundEntitiesByCounty: Array<EntityGeoLocation> = entitiesByCounty.filter((element: EntityGeoLocation) =>

                element
                && element.address
                && element.address.city
                && element.address.city.parent
                && element.address.city.parent.name
                && element.address.city.parent.name.toLowerCase() == selectedCountyNameString.replace(".", "").toLowerCase()
            );

            let foundEntitiesByServiesInCounties: Array<EntityGeoLocation> = entitiesByCounty.filter((element: EntityGeoLocation) =>

                element
                && element.servesInCounties
                && element.servesInCounties.some((countyGeo) => countyGeo
                                                                && countyGeo.name
                                                                && countyGeo.name.toLowerCase() == selectedCountyNameString.replace(".", "").toLowerCase()
            ));

            foundEntitiesByCounty = foundEntitiesByCounty.concat(foundEntitiesByServiesInCounties);

            if (foundEntitiesByCounty && foundEntitiesByCounty.length > 0) {

                // Build foundOptions as before
                let foundOptions: Array<entityListItem> = [];

                for (var j = 0; j < foundEntitiesByCounty.length; j++) {
                    let entityOption: entityListItem =
                    {
                        value: foundEntitiesByCounty[j].code,
                        name: foundEntitiesByCounty[j].name,
                        entityType: getEntityType(foundEntitiesByCounty[j].code)
                    };

                    if (!foundOptions.find(e => e.value == entityOption.value)) {
                        foundOptions.push(entityOption);
                    }
                }

                // Separate the matching option and the rest
                const matchingOption = foundOptions.find(
                    o => o.name.toLowerCase() === selectedCountyNameString.toLowerCase()
                );

                const otherOptions = foundOptions
                    .filter(o => o !== matchingOption)
                    .sort((a, b) => a.name.localeCompare(b.name));

                // Combine: default, matching (if any), then sorted others
                const sortedOptions = [
                    ...(matchingOption ? [matchingOption] : []),
                    ...otherOptions
                ];

                setSelectedCountyName(selectedCountyNameString);

                if (censusPopulation && censusPopulation.length > 0) {

                    var foundCountyGeolocation = entitiesByCounty
                        .find((element => element.name.toLowerCase() == selectedCountyNameString.replace(".", "").toLowerCase()));

                    if (foundCountyGeolocation) {
                        let foundGeolocationCode = foundCountyGeolocation.code;
                        var foundPopulation = censusPopulation.find((countyPops => countyPops.code == foundGeolocationCode));
                        if (foundPopulation) {
                            setSelectedCensusPopulation(foundPopulation.populationSize.toLocaleString());
                        } else {
                            setSelectedCensusPopulation("");
                        }
                    } else {

                        if (foundEntitiesByCounty[0]
                            && foundEntitiesByCounty[0].address
                            && foundEntitiesByCounty[0].address.city
                            && foundEntitiesByCounty[0].address.city.parent
                            && foundEntitiesByCounty[0].address.city.parent.name
                            && foundEntitiesByCounty[0].address.city.parent.name.toLowerCase() == selectedCountyNameString.replace(".", "").toLowerCase()) {

                            var firstRollupEntityCode = foundEntitiesByCounty[0].address.city.parent.code;
                            var foundPopulationFromParentNode = censusPopulation.find((countyPops => countyPops.code == firstRollupEntityCode));

                            if (foundPopulationFromParentNode) {
                                setSelectedCensusPopulation(foundPopulationFromParentNode.populationSize.toLocaleString());
                            } else {
                                setSelectedCensusPopulation("");
                            }
                        } else {
                            setSelectedCensusPopulation("");
                        }
                    }

                } else {
                    setSelectedCensusPopulation("");
                }
                

                if (sortedOptions && sortedOptions.length > 0) {
                    setExistingEntityOptions(sortedOptions);
                    setSearchResults(sortedOptions);
                } else {
                    setExistingEntityOptions([]);
                    setSearchResults([]);
                }
            }

            setModalIsOpen(true);
      
    };

    //constants
    const projection = geoAlbers()
        .scale(16250)
        .translate([width / 2, (height / 2) - 150]) // Move up by 150px
        .rotate([88, 0])
        .center([2, 30]);

    const path = geoPath(projection);

    const getEntityType = (code: string): "COUNTY" | "CITY" | "SPECIAL_DISTRICT" | "OTHER_ENTITY" | "UNKNOWN" => {

        let result: "COUNTY" | "CITY" | "SPECIAL_DISTRICT" | "OTHER_ENTITY" | "UNKNOWN" = "UNKNOWN";

        const intCode = parseInt(code, 10);

        if (!isNaN(intCode)) {
            if (intCode >= 100000 && intCode <= 199999) {
                result = "COUNTY";
            }
            else if (intCode >= 200000 && intCode <= 299999) {
                result = "CITY";
            }
            else if (intCode >= 300000 && intCode <= 399999) {
                result = "SPECIAL_DISTRICT";
            }
            else if (intCode >= 500000 && intCode <= 599999) {
                result = "OTHER_ENTITY";
            }
        }

        return result;
    }

    const searchEntityNames = (inputName: string) => {

        setCountiesAreCollapsed(false);
        setCitiesAreCollapsed(false);
        setSpecialDistrictsAreCollapsed(false);
        setOtherEntitiesAreCollapsed(false);

        setEntityName(inputName);

        const fuse = new Fuse(existingEntityOptions, {
            keys: ["name"],
            shouldSort: true,
            threshold: .3,
            ignoreLocation: true,
        });

        const result = fuse.search(inputName);
        let finalResult: entityListItem[] = [];
        if (result.length && result.length > 0) {
            for (var i = 0; i < result.length; i++) {
                finalResult.push({ name: result[i].item.name, value: result[i].item.value, entityType: result[i].item.entityType });
            }
        } else if (result.length == 0 && inputName == '') {
            finalResult = existingEntityOptions;
        }
        setSearchResults(finalResult.sort((a: any, b: any) => a.name.localeCompare(b.name)));

    };

    const initializeSelectedEntityFromModal = () => {
        props.setPreSelectedEntityList([selectedEntityCode]);

        var foundEntityOption = entityOptions.find((element: { label: string; value: string; }) => element.value == selectedEntityCode);

        if (foundEntityOption) {
            onSelectChangeValue(foundEntityOption, null);
        }
        toggleModal();
    }

    const getIsEntityTypeCollapsed = (entityTypeValue: "COUNTY" | "CITY" | "SPECIAL_DISTRICT" | "OTHER_ENTITY" | "UNKNOWN"): boolean => {
        let result: boolean = true;

        switch (entityTypeValue) {

            case "COUNTY":
                result = countiesAreCollapsed;
                break;
            case "CITY":
                result = citiesAreCollapsed;
                break;
            case "SPECIAL_DISTRICT":
                result = specialDistrictsAreCollapsed;
                break;
            case "OTHER_ENTITY":
                result = otherEntitiesAreCollapsed;
                break;
            default:
                result = true;
                break;
        }

        return result;
    }

    const toggleEntityTypeCollapse = (entityTypeValue: "COUNTY" | "CITY" | "SPECIAL_DISTRICT" | "OTHER_ENTITY" | "UNKNOWN"): void => {

        switch (entityTypeValue) {

            case "COUNTY":
                setCountiesAreCollapsed(!countiesAreCollapsed);
                break;
            case "CITY":
                setCitiesAreCollapsed(!citiesAreCollapsed);
                break;
            case "SPECIAL_DISTRICT":
                setSpecialDistrictsAreCollapsed(!specialDistrictsAreCollapsed);
                break;
            case "OTHER_ENTITY":
                setOtherEntitiesAreCollapsed(!otherEntitiesAreCollapsed);
                break;
            default:
                break;
        }
    }

    const getTextOffSetGivenCountyName = (countyName: string): Number => {

        let result: Number = 0;

        switch (countyName) {

            case "Escambia":
                result = 30;
                break;

            case "Okaloosa":
            case "Suwannee":
            case "St. Lucie":
            case "Jefferson":
                result = 20;
                break;

            case "Bradford":
                result = 10;
                break;

            case "Santa Rosa":
            case "Walton":
            case "Union":
                result = -10;
                break;

            case "Hillsborough":
            case "Okeechobee":
                result = -20;
                break;

            default:
                break;
        }

        return result;
    }

    const switchReportModuleAndSetEntityList = () => {
        initializeSelectedEntityFromModal();
        props.setSelectedReportsModule('ADHOC');
    };

    const createSortKey = function (a: { value: string; label: string }): string {
        if (entitySortKeys.hasOwnProperty(a.label)) {
            return entitySortKeys[a.label];
        }
        var prefix: string = "f";
        var parts = a.label.split(" - ");
        if (parts.length < 2) {
            prefix = "a";
        } else {
            switch (parts[0]) {
                case "City":
                    prefix = "c";
                    break;

                case "County":
                    prefix = "b";
                    break;

                case "Special District":
                    prefix = "d";
                    break;

                case "Other Entity":
                    prefix = "e";
                    break;

                default:
                    break;
            }
        }
        var result = prefix + parts[1];
        entitySortKeys[a.label] = result;
        return result;
    }

    const sortEntityOptions = function (a: { value: string; label: string }, b: { value: string; label: string }): number {
        var akey: string = createSortKey(a);
        var bkey: string = createSortKey(b);
        var result = akey.localeCompare(bkey);
        return result;
    }

    const onSelectChangeValue = (e: { value: string; label: string }, action: any) => {

        setSelectedDropDownItem(e);
        var eQuery: EditEntityQuery = {
            entityId: e.value + "*",
            taxonomyYear: -1
        };
        dispatch(loadEntity(eQuery));
        dispatch(resetState({}));
        dispatch(resetConceptState({}));
        dispatch(setIsDirty({ IsDirty: false, Page: "" }))
    }

    const loadEntityCountyData = async () => {
        const response = await dispatch(getEntityGeoLocations());
        let entitiesByCountyGeoLocation: Array<EntityGeoLocation> | null = unwrapResult(response);

        if (entitiesByCountyGeoLocation && entitiesByCountyGeoLocation.length > 0) {

            //set entities for county rollup
            var entitiesWithGeoLocations = entitiesByCountyGeoLocation.filter((element: EntityGeoLocation) => Number(element.code[0]) != 9
                && (element.address != null || element.servesInCounties != null));

            setEntitiesByCounty(entitiesWithGeoLocations);

            //set entities for drop down list
            let result: Array<{ value: string; label: string }> = [];
            let allEntitiesList: EntityGeoLocation[] = entitiesByCountyGeoLocation.filter((element: EntityGeoLocation) => Number(element.code[0]) != 9);

            for (var i = 0; i < allEntitiesList.length; i++) {

                let entityOption: { value: string; label: string } =
                {
                    label: utilityHelper.getEntityTypeText(allEntitiesList[i].code) + " - " + allEntitiesList[i].name,
                    value: allEntitiesList[i].code
                }

                result.push(entityOption)
            }

            result.sort(sortEntityOptions);
            setEntityOptions(result);

        } else {
            setEntitiesByCounty([]);
            setEntityOptions([]);
        }
        setIsLoading(false); // Done loading
    }

    const getDoingBusinessAsNameGivenCode = ( ): string => {

        let result: string = "";
        if ((afrSelector && afrSelector.owner)
            && (selectedEntitySelector && selectedEntitySelector.doingBusinessAs)) {

            if (selectedEntitySelector.code === selectedEntitySelector.doingBusinessAs.currentEntity) {

                if (selectedEntitySelector.doingBusinessAs.previousEntity) {

                    let previousEntityCode: string = selectedEntitySelector.doingBusinessAs.previousEntity;
                    var foundPreviousListItem = entityOptions
                                                .find((element: { label: string; value: string; }) => element.value == previousEntityCode);

                    if (foundPreviousListItem) {
                        var labelPartsPrevious = foundPreviousListItem.label.split(" - ");
                        result = " was previously doing business as " + labelPartsPrevious[1];
                    }
                }
                
            } else {
                if (selectedEntitySelector.doingBusinessAs.currentEntity) {
                    let currentEntityCode: string = selectedEntitySelector.doingBusinessAs.currentEntity;
                    var foundCurrentListItem = entityOptions
                                                .find((element: { label: string; value: string; }) => element.value == currentEntityCode);

                    if (foundCurrentListItem) {
                        var labelPartsCurrent = foundCurrentListItem.label.split(" - ");
                        result = " is currently doing business as " + labelPartsCurrent[1];
                    }
                }              
            }        
        }

        return result;            
    }

    const getRenderGivenCount = (count: number, singularLabel: string, pluralLabel: string, entityTypeValue: "COUNTY" | "CITY" | "SPECIAL_DISTRICT" | "OTHER_ENTITY" | "UNKNOWN"): JSX.Element => {

        return count == 1 ? (
            <React.Fragment>
                <br />
                <Row>
                    <Col md="1"></Col>
                    <Col md="11">
                        <Label>{singularLabel}: </Label>
                    </Col>
                </Row>
                <Row>
                    <Col md="2"></Col>
                    <Col md="10">
                        <ListGroup>
                            {
                                searchResults.filter((item: entityListItem) => item.entityType == entityTypeValue).map((item: entityListItem) => (

                                    <ListGroupItem
                                        key={item.value}
                                        active={selectedEntityCode === item.value}
                                        onClick={() => {
                                            if (selectedEntityCode != item.value) { setSelectedEntityCode(item.value); setSelectedEntityNameValue(item.name); }
                                        }}>{item.name}</ListGroupItem>

                                ))
                            }
                        </ListGroup>
                    </Col>
                </Row>
            </React.Fragment>)
            : count > 1 ? (
                <React.Fragment>
                    <br />
                    <Row onClick={() => { toggleEntityTypeCollapse(entityTypeValue) }}>
                        <Col md="1">
                            {getIsEntityTypeCollapsed(entityTypeValue) ?
                                (<FontAwesomeIcon icon={faCaretRight} color="rgb(119, 152, 187)" />)
                                :
                                (<FontAwesomeIcon icon={faCaretDown} color="rgb(119, 152, 187)" />)
                            }
                        </Col>
                        <Col md="11">
                            <Label>{pluralLabel} ({count}): </Label>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="2"></Col>
                        <Col md="10">
                            <Collapse isOpen={getIsEntityTypeCollapsed(entityTypeValue) == false}>
                                <ListGroup>
                                    {
                                        searchResults.filter((item: entityListItem) => item.entityType == entityTypeValue).map((item: entityListItem) => (

                                            <ListGroupItem
                                                key={item.value}
                                                active={selectedEntityCode === item.value}
                                                onClick={() => {
                                                    if (selectedEntityCode != item.value) { setSelectedEntityCode(item.value); setSelectedEntityNameValue(item.name); }
                                                }}>{item.name}</ListGroupItem>

                                        ))
                                    }
                                </ListGroup>
                            </Collapse>
                        </Col>
                    </Row>
                </React.Fragment>)
                : <></>;
    }

    const getRenderGivenCountAlwaysShowCount = (count: number, singularLabel: string, pluralLabel: string, entityTypeValue: "COUNTY" | "CITY" | "SPECIAL_DISTRICT" | "OTHER_ENTITY" | "UNKNOWN"): JSX.Element => {

        return count == 1 ? (
            <React.Fragment>
                <br />
                <Row onClick={() => { toggleEntityTypeCollapse(entityTypeValue) }}>
                    <Col md="1">
                        {getIsEntityTypeCollapsed(entityTypeValue) ?
                            (<FontAwesomeIcon icon={faCaretRight} color="rgb(119, 152, 187)" />)
                            :
                            (<FontAwesomeIcon icon={faCaretDown} color="rgb(119, 152, 187)" />)
                        }
                    </Col>
                    <Col md="11">
                        <Label>{singularLabel} ({count}): </Label>
                    </Col>
                </Row>
                <Row>
                    <Col md="2"></Col>
                    <Col md="10">
                        <Collapse isOpen={getIsEntityTypeCollapsed(entityTypeValue) == false}>
                            <ListGroup>
                                {
                                    searchResults.filter((item: entityListItem) => item.entityType == entityTypeValue).map((item: entityListItem) => (

                                        <ListGroupItem
                                            key={item.value}
                                            active={selectedEntityCode === item.value}
                                            onClick={() => {
                                                if (selectedEntityCode != item.value) { setSelectedEntityCode(item.value); setSelectedEntityNameValue(item.name); }
                                            }}>{item.name}</ListGroupItem>

                                    ))
                                }
                            </ListGroup>
                        </Collapse>
                    </Col>
                </Row>
            </React.Fragment>)
            : count > 1 ? (
                <React.Fragment>
                    <br />
                    <Row onClick={() => { toggleEntityTypeCollapse(entityTypeValue) }}>
                        <Col md="1">
                            {getIsEntityTypeCollapsed(entityTypeValue) ?
                                (<FontAwesomeIcon icon={faCaretRight} color="rgb(119, 152, 187)" />)
                                :
                                (<FontAwesomeIcon icon={faCaretDown} color="rgb(119, 152, 187)" />)
                            }
                        </Col>
                        <Col md="11">
                            <Label>{pluralLabel} ({count}): </Label>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="2"></Col>
                        <Col md="10">
                            <Collapse isOpen={getIsEntityTypeCollapsed(entityTypeValue) == false}>
                                <ListGroup>
                                    {
                                        searchResults.filter((item: entityListItem) => item.entityType == entityTypeValue).map((item: entityListItem) => (

                                            <ListGroupItem
                                                key={item.value}
                                                active={selectedEntityCode === item.value}
                                                onClick={() => {
                                                    if (selectedEntityCode != item.value) { setSelectedEntityCode(item.value); setSelectedEntityNameValue(item.name); }
                                                }}>{item.name}</ListGroupItem>

                                        ))
                                    }
                                </ListGroup>
                            </Collapse>
                        </Col>
                    </Row>
                </React.Fragment>)
                : <></>;
    }

    const countiesRender = getRenderGivenCount(countyCount, "County", "Counties", "COUNTY");

    const citiesRender = getRenderGivenCountAlwaysShowCount(cityCount, "City", "Cities", "CITY");

    const specialDistrictsRender = getRenderGivenCountAlwaysShowCount(specialDistrictCount, "Special District", "Special Districts", "SPECIAL_DISTRICT");

    const otherEntitiesRender = getRenderGivenCountAlwaysShowCount(otherEntityCount, "Other Entity", "Other Entities", "OTHER_ENTITY");

    const loadCountyPopulationData = async () => {

        const response = await dispatch(getCountyPopulations());
        let countyPopulations: Array<CountyPopulation> | null = unwrapResult(response);
        if (countyPopulations && countyPopulations.length > 0) {
            setCensusPopulation(countyPopulations);
        } else {
            setCensusPopulation([]);
        }
    }

    //side effects
    React.useEffect(() => {
        setIsLoading(true); // Start loading
        postProcessJSON();
    }, []);

    React.useEffect(() => {
        // We will need to refactor this component so the taxonomy type can be passed in instead of assumed. That's for 3.1
        dispatch(setTaxonomyType(TaxonomyTypeEnum.AFR));
        // reset the states when destroying the component
        return function cleanup() {
            dispatch(resetState({}));
            dispatch(resetEntityState({}));
            dispatch(resetConceptState({}));
        }
    }, []);

    React.useEffect(() => {

        if (mapData && mapData.length > 0) {
            loadEntityCountyData();
        }

    }, [mapData]);

    React.useEffect(() => {
        if (geoJSONSelector) {

            var arrayOfFeatures: any = [];
            for (var i = 0; i < geoJSONSelector.features.length; i++) {

                const featureWithAccount = JSON.parse(JSON.stringify(geoJSONSelector.features[i]));
                featureWithAccount.properties.ACCOUNT_VALUE = 0;
                arrayOfFeatures.push(featureWithAccount);
            }

            setMapData(arrayOfFeatures);
        }
    }, [geoJSONSelector]);


    React.useEffect(() => {

        let count: number = 0;

        count = searchResults.filter(item => item.entityType === "COUNTY").length;
        setCountyCount(count);

        count = searchResults.filter(item => item.entityType === "CITY").length;
        setCityCount(count);

        count = searchResults.filter(item => item.entityType === "SPECIAL_DISTRICT").length;
        setSpecialDistrictCount(count);

        count = searchResults.filter(item => item.entityType === "OTHER_ENTITY").length;
        setOtherEntityCount(count);

    }, [searchResults]);

    React.useEffect(() => {

        if (modalIsOpen == false) {
            setEntityName('');
            setExistingEntityOptions([]);
            setSearchResults([]);
            setSelectedCountyName("");
            setSelectedEntityCode("");
            setSelectedEntityNameValue("");
            setSelectedCensusPopulation("");
            setCountiesAreCollapsed(true);
            setCitiesAreCollapsed(true);
            setSpecialDistrictsAreCollapsed(true);
            setOtherEntitiesAreCollapsed(true);

            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 250);
        }

    }, [modalIsOpen]);

    React.useEffect(() => {

        if(censusPopulation == null) {
            loadCountyPopulationData();
        }

    }, [censusPopulation]);

    return (
        <React.Fragment>
            <Row>
                <Col md="9" style={{ position: "relative" }}>
                    {/* Loading Banner */}
                    {isLoading && (
                        <div className="loading-banner">
                            <span className="loading-spinner">
                                {/*<FontAwesomeIcon icon={faCaretRight} />*/}
                            </span>
                            Loading Florida Map & Entities...
                        </div>
                    )}
                    <div style={{ position: "relative", height: "100%", overflowY: "auto" }}>
                        {/* SVG Map */}
                        <svg
                            ref={svgRef}
                            className="map-geocanvas"
                            width="100%"
                            height="100%"
                            viewBox="0 0 2000 2000"
                            style={{ display: "block" }}
                        >
                            <g className="geocounties">
                                {/* Render all paths first */}
                                {mapData.map((feature: any, i: number) => {
                                    const dValue = path(feature);
                                    if (!dValue) return null;
                                    return (
                                        <path
                                            key={`path-${i}`}
                                            d={dValue}
                                            onMouseOver={() => { if (!isLoading) handleMouseOver(feature.properties); }}
                                            onMouseOut={handleMouseOut}
                                            onClick={() => { if (!isLoading) handlePathClick(i); }}
                                            className="geocounties"
                                            fill={handleFill(feature)}
                                            style={{ cursor: isLoading ? "not-allowed" : "pointer" }}
                                        />
                                    );
                                })}
                                {/* Render all text labels after paths */}
                                {mapData.map((feature: any, i: number) => {
                                    const centroid = path.centroid(feature);
                                    if (!centroid || isNaN(centroid[0]) || isNaN(centroid[1])) return null;
                                    return (
                                        <text
                                            key={`text-${i}`}
                                            x={centroid[0]}
                                            y={centroid[1] + getTextOffSetGivenCountyName(feature.properties.NAME)}
                                            textAnchor="middle"
                                            alignmentBaseline="central"
                                            fontSize="18"
                                            fill="#222"
                                            pointerEvents="none"
                                            className="map-label"
                                        >
                                            {feature.properties.NAME}
                                        </text>
                                    );
                                })}
                            </g>
                        </svg>
                    </div>
                </Col>
                <Col md="3">
                    <Row>
                        <Col md="12">
                            <CardHeader
                                style={{
                                    color: "white",
                                    backgroundColor: "#458BCA",
                                    borderRadius: "0px",
                                    fontWeight: "initial"
                                }}
                            >Entity: <i>Select an entity from the map or search below</i></CardHeader>
                            <Card>
                                <CardBody style={{
                                    fontSize: ".9rem"
                                }}>
                                    <Select
                                        isSearchable={true}
                                        isDisabled={entityStateStatusSelector != "idle"}
                                        onChange={onSelectChangeValue}
                                        options={entityOptions}
                                        value={selectedDropDownItem}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="12">
                            <CardHeader
                                style={{
                                    color: "white",
                                    backgroundColor: "#458BCA",
                                    borderRadius: "0px",
                                    fontWeight: "initial"
                                }}
                            >AFR Year</CardHeader>
                            <Card>
                                <CardBody style={{
                                    fontSize: ".9rem"
                                }}>
                                    <AfrSelectorDropDown publicreports={true} />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            {afrSelector && (<>
                                {selectedEntitySelector && hasNameChange(selectedEntitySelector) && (
                                    <>
                                        <CardHeader
                                            style={{
                                                color: "white",
                                                backgroundColor: "#458BCA",
                                                borderRadius: "0px",
                                                fontWeight: "initial"
                                            }}
                                        >Name Change</CardHeader>
                                        <Card>
                                            <CardBody style={{
                                                fontSize: ".9rem"
                                            }}>
                                                <div><span style={{ fontWeight: "bolder" }}>{selectedEntitySelector.name}</span>{getNameChangeText(selectedEntitySelector)}</div>
                                            </CardBody>
                                        </Card>
                                    </>
                                )}
                                {selectedEntitySelector && afrSelector && afrSelector.owner && matchesAffiliation(selectedEntitySelector, afrSelector) && (
                                    <>
                                        <CardHeader
                                            style={{
                                                color: "white",
                                                backgroundColor: "#458BCA",
                                                borderRadius: "0px",
                                                fontWeight: "initial"
                                            }}
                                        >Dependent</CardHeader>
                                        <Card>
                                            <CardBody style={{
                                                fontSize: ".9rem"
                                            }}>
                                                <div><span style={{ fontWeight: "bolder" }}>{selectedEntitySelector.name}</span> is a {selectedEntitySelector.districtDependency.toLowerCase()} {selectedEntitySelector.districttype} of {afrSelector.owner.name}. Its Financial Information is included in the Affiliated Entities Section of the primary Annual Financial Report.</div>
                                            </CardBody>
                                        </Card>
                                    </>
                                )}
                                {selectedEntitySelector && selectedEntitySelector.doingBusinessAs && (
                                    <>
                                        <CardHeader
                                            style={{
                                                color: "white",
                                                backgroundColor: "#458BCA",
                                                borderRadius: "0px",
                                                fontWeight: "initial"
                                            }}
                                        >Doing Business As</CardHeader>
                                        <Card>
                                            <CardBody style={{
                                                fontSize: ".9rem"
                                            }}>
                                                <div>
                                                    <span style={{ fontWeight: "bolder" }}>
                                                        {selectedEntitySelector.name}
                                                    </span>
                                                    {getDoingBusinessAsNameGivenCode()}
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </>
                                )}
                                <CardHeader
                                    style={{
                                        color: "white",
                                        backgroundColor: "#458BCA",
                                        borderRadius: "0px",
                                        fontWeight: "initial"
                                    }}
                                >Choose Report</CardHeader>
                                <Card>
                                    <CardBody style={{
                                        fontSize: ".9rem"
                                    }}>
                                        <AfrReportsCore publicreports={true} taxonomyType={TaxonomyTypeEnum.AFR} isForPublicReports={true} />
                                    </CardBody>
                                </Card> </>)}
                        </Col>
                    </Row>
                </Col>
            </Row>

            <Modal isOpen={modalIsOpen} className="authorizationModal">
                <ModalHeader
                    toggle={toggleModal}
                    style={{ border: "none" }}
                >
                    <Row>
                        <Col>
                            <h1><Label>County:  {selectedCountyName}</Label></h1>
                        </Col>
                    </Row>
                    {
                        selectedCensusPopulation && selectedCensusPopulation != "" ? (
                            <React.Fragment>
                                <Row>
                                    <Col>
                                        <Label>County Population:  {selectedCensusPopulation}<sup>*</sup></Label>
                                        <br />
                                        <Label style={{ fontSize: ".6em" }} ><sup>*</sup>Source: U.S. Census Bureau, Population Division</Label>
                                    </Col>
                                </Row>
                            </React.Fragment>
                        )
                            :
                            (<></>)
                    }

                              
                </ModalHeader>

                <ModalBody style={{ overflowY: "scroll" }}>
                    <Row style={{ border: "1px solid", borderColor: "black" }}>
                        <Col md="12">

                            <br />
                            <Row>
                                <Col>
                                    <Label style={{fontSize: "1.5em"}}>Selected Entity:  {selectedEntityNameValue}</Label>
                                </Col>
                            </Row>
                            <Row>
                                <Col style={{ position: "relative" }}>
                                    <Input
                                        type="text"
                                        value={entityName}
                                        onChange={(e) => { searchEntityNames(e.currentTarget.value) }}
                                        placeholder="Search For Entities..."
                                        style={{ paddingRight: "2.5em" }} // Add space for the X button
                                    />
                                    {entityName && (
                                        <span
                                            onClick={() => { searchEntityNames(""); }}
                                            style={{
                                                position: "absolute",
                                                right: "1em",
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                cursor: "pointer",
                                                color: "#888",
                                                fontSize: "1.2em",
                                                zIndex: 2
                                            }}
                                            title="Clear"
                                        >
                                            <FontAwesomeIcon icon={faXmark} />

                                        </span>
                                    )}
                                </Col>
                            </Row>

                            {countiesRender}
                            {citiesRender}
                            {specialDistrictsRender}
                            {otherEntitiesRender}

                            <br />

                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>

                    <Button color="primary" disabled={selectedEntityCode == ''}
                        onClick={() => { initializeSelectedEntityFromModal(); }}>Submitted AFR Documents</Button>

                    <Button color="primary" disabled={selectedEntityCode == ''}
                        onClick={() => { switchReportModuleAndSetEntityList() }}>Create Your Own Report</Button>

                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};

export default GeoLocationMap;